import {memo, useState} from "react";
import {Stack, HStack, Heading, Text, Box, Badge, For, Fieldset, Textarea} from "@chakra-ui/react";
import {Button} from "../../../../../components/ui/button";
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogCloseTrigger,
} from "../../../../../components/ui/dialog";
import {SparklesIcon, CheckIcon} from "@heroicons/react/16/solid";
import {NativeSelectRoot, NativeSelectField} from "../../../../../components/ui/native-select";
import {ProgressBar, ProgressRoot} from "../../../../../components/ui/progress";
import {Field} from "../../../../../components/ui/field";

interface GenerationWizardProps {
  open: boolean;
  onClose: () => void;
  onComplete?: () => void;
  isRegeneration?: boolean;
  existingPrompt?: string;
}

const diagramTypes = [
  "Business Process Flow",
  "IT Process Flow",
  "System Architecture",
  "Data Flow Diagram",
  "Control Flow Diagram",
];

const Step1ConfigurePrompt = memo(
  ({
    diagramType,
    setDiagramType,
    customInstructions,
    setCustomInstructions,
  }: {
    diagramType: string;
    setDiagramType: (type: string) => void;
    customInstructions: string;
    setCustomInstructions: (instructions: string) => void;
  }) => {
    return (
      <Stack gap={4}>
        <Box>
          <Heading size="md" mb={2}>
            Configure Generation
          </Heading>
          <Text fontSize="sm" color="fg.muted">
            Specify the type of diagram you want and provide any additional context or requirements
          </Text>
        </Box>

        <Field label="Diagram Type" required>
          <NativeSelectRoot>
            <NativeSelectField value={diagramType} onChange={e => setDiagramType(e.target.value)}>
              <For each={diagramTypes}>
                {type => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                )}
              </For>
            </NativeSelectField>
          </NativeSelectRoot>
        </Field>

        <Fieldset.Root>
          <Fieldset.Legend>Custom Instructions</Fieldset.Legend>
          <Fieldset.HelperText>
            Provide specific instructions for what you want to see in the diagram (e.g., "Focus on segregation of
            duties", "Show approval workflows", "Include system integrations")
          </Fieldset.HelperText>
          <Fieldset.Content>
            <Textarea
              value={customInstructions}
              onChange={e => setCustomInstructions(e.target.value)}
              placeholder="Enter your instructions here..."
              rows={6}
            />
          </Fieldset.Content>
        </Fieldset.Root>

        <Box p={3} bg="blue.50" borderRadius="md">
          <HStack gap={2} mb={1}>
            <SparklesIcon width={16} height={16} color="var(--chakra-colors-blue-600)" />
            <Text fontSize="sm" fontWeight="medium" color="blue.700">
              AI will analyze your sources and instructions
            </Text>
          </HStack>
          <Text fontSize="sm" color="blue.600">
            The AI will identify key processes, roles, systems, and controls from your selected documents, then
            structure them into a clear diagram following your specified type and instructions.
          </Text>
        </Box>
      </Stack>
    );
  },
);

const Step2Generating = memo(({progress}: {progress: number}) => {
  const steps = [
    {label: "Analyzing source documents", threshold: 20},
    {label: "Identifying key processes and roles", threshold: 40},
    {label: "Mapping relationships and flows", threshold: 60},
    {label: "Structuring diagram layout", threshold: 80},
    {label: "Finalizing and validating", threshold: 100},
  ];

  const currentStepIndex = steps.findIndex(step => progress < step.threshold);
  const activeStepIndex = currentStepIndex === -1 ? steps.length - 1 : currentStepIndex;

  return (
    <Stack gap={6}>
      <Box textAlign="center">
        <SparklesIcon width={48} height={48} color="var(--chakra-colors-blue-500)" style={{margin: "0 auto 16px"}} />
        <Heading size="md" mb={2}>
          Generating Your Diagram
        </Heading>
        <Text fontSize="sm" color="fg.muted">
          Please wait while AI analyzes your sources and creates the diagram
        </Text>
      </Box>

      <ProgressRoot value={progress} maxW="full">
        <ProgressBar colorPalette="blue" />
      </ProgressRoot>

      <Stack gap={3}>
        <For each={steps}>
          {(step, index) => (
            <HStack key={step.label} gap={3}>
              <Box
                w={6}
                h={6}
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
                bg={index <= activeStepIndex ? "blue.500" : "gray.200"}
                color="white"
              >
                {index < activeStepIndex ? (
                  <CheckIcon width={14} height={14} />
                ) : (
                  <Text fontSize="xs">{index + 1}</Text>
                )}
              </Box>
              <Text
                fontSize="sm"
                color={index <= activeStepIndex ? "fg.default" : "fg.muted"}
                fontWeight={index === activeStepIndex ? "medium" : "normal"}
              >
                {step.label}
              </Text>
            </HStack>
          )}
        </For>
      </Stack>
    </Stack>
  );
});

const Step3Preview = memo(() => {
  return (
    <Stack gap={4}>
      <Box textAlign="center">
        <Box
          w={12}
          h={12}
          borderRadius="full"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg="green.100"
          color="green.600"
          margin="0 auto 16px"
        >
          <CheckIcon width={24} height={24} />
        </Box>
        <Heading size="md" mb={2}>
          Diagram Generated Successfully!
        </Heading>
        <Text fontSize="sm" color="fg.muted">
          Your diagram has been created and is ready to view
        </Text>
      </Box>

      <Box p={4} bg="bg.subtle" borderRadius="md" borderWidth="1px">
        <Stack gap={2}>
          <HStack justifyContent="space-between">
            <Text fontSize="sm" fontWeight="medium">
              Sources Analyzed
            </Text>
            <Badge colorPalette="blue">4 documents</Badge>
          </HStack>
          <HStack justifyContent="space-between">
            <Text fontSize="sm" fontWeight="medium">
              Nodes Created
            </Text>
            <Badge>12 process steps</Badge>
          </HStack>
          <HStack justifyContent="space-between">
            <Text fontSize="sm" fontWeight="medium">
              Connections
            </Text>
            <Badge>15 flows</Badge>
          </HStack>
          <HStack justifyContent="space-between">
            <Text fontSize="sm" fontWeight="medium">
              Swimlanes
            </Text>
            <Badge>7 roles</Badge>
          </HStack>
        </Stack>
      </Box>

      <Box p={3} bg="blue.50" borderRadius="md">
        <Text fontSize="sm" color="blue.700">
          Click "View Diagram" to see your generated diagram. You can view the AI reasoning and source evidence in the
          AI tab.
        </Text>
      </Box>
    </Stack>
  );
});

export const GenerationWizard = memo(
  ({open, onClose, onComplete, isRegeneration = false, existingPrompt = ""}: GenerationWizardProps) => {
    const [step, setStep] = useState(1);
    const [diagramType, setDiagramType] = useState("Business Process Flow");
    const [customInstructions, setCustomInstructions] = useState(existingPrompt);
    const [generationProgress, setGenerationProgress] = useState(0);

    const handleNext = () => {
      if (step === 1) {
        // Start generation
        setStep(2);
        // Simulate generation progress
        let progress = 0;
        const interval = setInterval(() => {
          progress += 5;
          setGenerationProgress(progress);
          if (progress >= 100) {
            clearInterval(interval);
            setTimeout(() => setStep(3), 500);
          }
        }, 200);
      } else {
        setStep(step + 1);
      }
    };

    const handleComplete = () => {
      onComplete?.();
      onClose();
      // Reset wizard state
      setTimeout(() => {
        setStep(1);
        setGenerationProgress(0);
      }, 300);
    };

    const canProceed = () => {
      if (step === 1) return diagramType && customInstructions.trim().length > 0;
      return true;
    };

    return (
      <DialogRoot open={open} onOpenChange={e => !e.open && onClose()} size="lg">
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <HStack gap={2}>
                <SparklesIcon width={20} height={20} />
                <Text>{isRegeneration ? "Regenerate Diagram with AI" : "Generate Diagram with AI"}</Text>
              </HStack>
            </DialogTitle>
            <DialogCloseTrigger />
          </DialogHeader>

          <DialogBody pb={6}>
            {step === 1 && (
              <Step1ConfigurePrompt
                diagramType={diagramType}
                setDiagramType={setDiagramType}
                customInstructions={customInstructions}
                setCustomInstructions={setCustomInstructions}
              />
            )}
            {step === 2 && <Step2Generating progress={generationProgress} />}
            {step === 3 && <Step3Preview />}
          </DialogBody>

          <DialogFooter>
            <HStack gap={3} width="full" justifyContent="space-between">
              <Text fontSize="sm" color="fg.muted">
                Step {step} of 3
              </Text>
              <HStack gap={3}>
                {step === 1 && (
                  <Button colorPalette="blue" onClick={handleNext} disabled={!canProceed()}>
                    Generate
                  </Button>
                )}
                {step === 3 && (
                  <Button colorPalette="blue" onClick={handleComplete}>
                    View Diagram
                  </Button>
                )}
              </HStack>
            </HStack>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    );
  },
);
